import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { makeApiCall } from '../utils/customerUtils'
import LoadingSpinner from '../components/LoadingSpinner'
import DealEditModal from '../components/DealEditModal'

interface Deal {
  id: number
  name: string
  title: string  // DealEditModal expects 'title' field
  amount: number
  customer_name?: string
  customer_email?: string
  customer_phone?: string
  customer_id: number  // Required for DealEditModal
  status_name: string
  status_color: string
  status_id: string   // Required for DealEditModal
  type_name?: string
  deal_type_id?: string  // Required for DealEditModal
  user_name: string
  user_email: string
  created_by: number  // User ID of the deal owner
  expected_close_date?: string
  closing_date?: string  // DealEditModal expects this
  base_points: string   // Required for DealEditModal
  bonus_points: string  // Required for DealEditModal
  volume_points: string // Required for DealEditModal
  position: number      // Required for DealEditModal
  description?: string  // Optional for DealEditModal
  commission: {
    totalPoints: number
    totalPercentage: number
    grossCommission: number
    agencyFee: number
    closingFeeAmount: number
    netCommission: number
  }
  created_at: string
  updated_at: string
}

interface PaginationInfo {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

const AdminDeals: React.FC = () => {
  const { user, isAdmin } = useAuth()
  const [deals, setDeals] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [editingDeal, setEditingDeal] = useState<any | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [dealOwnerStatuses, setDealOwnerStatuses] = useState<any[]>([])
  const [loadingStatuses, setLoadingStatuses] = useState(false)

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <p className="text-red-600 dark:text-red-400 font-medium">Access denied. Admin privileges required.</p>
        </div>
      </div>
    )
  }

  const fetchDeals = async (offset = 0) => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall(`api/admin/deals?limit=${pagination.limit}&offset=${offset}`)
      
      if (response.ok) {
        const data = await response.json()
        setDeals(data.deals)
        setPagination(data.pagination)
      } else {
        throw new Error('Failed to fetch deals')
      }
    } catch (error) {
      console.error('Error fetching admin deals:', error)
      setError('Failed to load deals. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDeals()
  }, [])

  const fetchDealOwnerStatuses = async (userId: number) => {
    try {
      setLoadingStatuses(true)
      const response = await makeApiCall(`api/admin/users/${userId}/deal-statuses`)
      
      if (response.ok) {
        const data = await response.json()
        setDealOwnerStatuses(data.statuses || [])
        return data.statuses || []
      } else {
        console.error('Failed to fetch deal owner statuses')
        setDealOwnerStatuses([])
        return []
      }
    } catch (error) {
      console.error('Error fetching deal owner statuses:', error)
      setDealOwnerStatuses([])
      return []
    } finally {
      setLoadingStatuses(false)
    }
  }

  const handleEditDeal = async (deal: Deal) => {
    // Fetch the deal owner's statuses first
    await fetchDealOwnerStatuses(deal.created_by)
    
    // Convert the deal to match DealEditModal expectations
    const modalDeal = {
      ...deal,
      title: deal.name,  // Map name to title for the modal
      amount: deal.amount.toString(),
      customer_id: deal.customer_id || 0,
      deal_type_id: deal.deal_type_id || '',
      status_id: deal.status_id || '',
      base_points: deal.base_points || '0',
      bonus_points: deal.bonus_points || '0',
      volume_points: deal.volume_points || '0',
      position: deal.position || 0,
      closing_date: deal.expected_close_date || '',
      customer_name: deal.customer_name || '',
      customer_email: deal.customer_email || '',
      customer_phone: deal.customer_phone || '',
      status_name: deal.status_name,
      status_color: deal.status_color,
      type_name: deal.type_name,
      description: deal.description,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    setEditingDeal(modalDeal)
    setShowEditModal(true)
  }

  const handleSaveDeal = async (updatedDeal: any) => {
    try {
      const response = await makeApiCall(`api/admin/deals/${updatedDeal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: updatedDeal.title,
          amount: typeof updatedDeal.amount === 'string' ? parseFloat(updatedDeal.amount) : updatedDeal.amount,
          customer_id: updatedDeal.customer_id,
          deal_type_id: updatedDeal.deal_type_id,
          status_id: updatedDeal.status_id,
          base_points: typeof updatedDeal.base_points === 'string' ? parseInt(updatedDeal.base_points) : updatedDeal.base_points,
          bonus_points: typeof updatedDeal.bonus_points === 'string' ? parseInt(updatedDeal.bonus_points) : updatedDeal.bonus_points,
          volume_points: typeof updatedDeal.volume_points === 'string' ? parseInt(updatedDeal.volume_points) : updatedDeal.volume_points,
          closing_date: updatedDeal.closing_date || null,
          description: updatedDeal.description
        })
      })

      if (response.ok) {
        const data = await response.json()
        // Update the deal in the list
        setDeals(deals.map(deal => 
          deal.id === updatedDeal.id ? { 
            ...data.deal, 
            title: data.deal.name,  // Map back for consistency
            customer_id: data.deal.customer_id || deal.customer_id,
            status_id: data.deal.status_id || deal.status_id,
            deal_type_id: data.deal.deal_type_id || deal.deal_type_id,
            base_points: (data.deal.base_points || 0).toString(),
            bonus_points: (data.deal.bonus_points || 0).toString(),
            volume_points: (data.deal.volume_points || 0).toString(),
            position: data.deal.position || deal.position
          } : deal
        ))
        setShowEditModal(false)
        setEditingDeal(null)
        alert('Deal updated successfully')
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update deal')
      }
    } catch (error) {
      console.error('Error updating deal:', error)
      alert(`Failed to update deal: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleDeleteDeal = async () => {
    if (!selectedDeal) return

    try {
      setDeleting(true)
      
      const response = await makeApiCall(`api/admin/deals/${selectedDeal.id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setDeals(deals.filter(deal => deal.id !== selectedDeal.id))
        setShowDeleteModal(false)
        setSelectedDeal(null)
        
        // Show success message
        alert('Deal deleted successfully')
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete deal')
      }
    } catch (error) {
      console.error('Error deleting deal:', error)
      alert(`Failed to delete deal: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setDeleting(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', { 
      style: 'currency', 
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-CA')
  }

  if (loading && deals.length === 0) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">All Deals</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage all deals across all users. Total: {pagination.total} deals
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Deals Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Deal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {deals.map((deal) => (
                <tr key={deal.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {deal.name}
                      </div>
                      {deal.type_name && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {deal.type_name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {deal.customer_name || 'N/A'}
                      </div>
                      {deal.customer_email && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {deal.customer_email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(deal.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                      style={{ backgroundColor: deal.status_color }}
                    >
                      {deal.status_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {deal.user_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {deal.user_email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(deal.commission.netCommission)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {deal.commission.totalPoints} pts
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(deal.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEditDeal(deal)}
                        className="text-gray-900 hover:text-blue-900 dark:text-gray-600 dark:hover:text-blue-300 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDeal(deal)
                          setShowDeleteModal(true)
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {deals.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No deals found.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} deals
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => fetchDeals(Math.max(0, pagination.offset - pagination.limit))}
              disabled={pagination.offset === 0}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Previous
            </button>
            <button
              onClick={() => fetchDeals(pagination.offset + pagination.limit)}
              disabled={!pagination.hasMore}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Deal
            </h3>
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Are you sure you want to permanently delete this deal?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <p className="font-medium text-gray-900 dark:text-white">{selectedDeal.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Customer: {selectedDeal.customer_name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Owner: {selectedDeal.user_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Amount: {formatCurrency(selectedDeal.amount)}
                </p>
              </div>
              <p className="text-red-600 dark:text-red-400 text-sm mt-2 font-medium">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedDeal(null)
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDeal}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Deal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Edit Modal */}
      {showEditModal && editingDeal && (
        <DealEditModal
          deal={editingDeal}
          isOpen={showEditModal}
          onSave={handleSaveDeal}
          onDelete={(deal: any) => {
            setEditingDeal(null)
            setShowEditModal(false)
            setSelectedDeal(deal as Deal)
            setShowDeleteModal(true)
          }}
          onClose={() => {
            setShowEditModal(false)
            setEditingDeal(null)
            setDealOwnerStatuses([]) // Clear statuses on close
          }}
          readOnly={false}
          statuses={dealOwnerStatuses}
        />
      )}
    </div>
  )
}

export default AdminDeals 